import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Receipt, ChevronLeft, ChevronRight, Loader2
} from 'lucide-react';
import { logsServices } from '@/services/logs.service';
import { queryKeys } from '@/services/queryKeyFactory';
import { usePlans } from '@/contexts/PlanRulesContext';
import { formatCurrency, formatDate } from '@/helpers/formatters';
import UpgradeModal from '../modals/UpgradeModal';
import PlanInfoCard from '../cards/PlanInfoCard';
import DashboardError from '../error/DashboardError';


const Invoices = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [targetPlan, setTargetPlan] = useState(null);
  const itemsPerPage = 10;

  const { isLoadingPlanRules, isErrorPlanRules, planRules, plans, } = usePlans()


  const { data: invoices = [], isLoading: invoicesLoading, isError: invoicesError } = useQuery({
    queryKey: queryKeys.paymentHistory(),
    queryFn: logsServices.getInvoices,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000
  });


  const currentPlan = useMemo(() => {
    if (isLoadingPlanRules || isErrorPlanRules || !planRules) return null
    return planRules?.plan_info
  }, [isLoadingPlanRules, isErrorPlanRules, planRules])

  // ── Plan change handlers ────────────────────────────────────────────────────
 

  const handleConfirmPlanChange = () => {
    if (!targetPlan) return;
    planChangeMutation.mutate(targetPlan.key);
  };


  
  // ── Badges / Formatters ─────────────────────────────────────────────────────
  const getStatusBadge = (status) => {
    switch (status) {
      case 'charged': return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Success</Badge>;
      case 'Pending': return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>;
      case 'Failed': return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Failed</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };



  const totalPages = Math.ceil(invoices?.data?.length / itemsPerPage);
  const paginatedInvoices = invoices?.data?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);



  if (invoicesLoading || isLoadingPlanRules) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading invoices...</p>
        </div>
      </div>
    );
  }

  if (isErrorPlanRules || invoicesError) {
    return <DashboardError
      title='Unable to load Invoices'
      section='Invoices'
    />
  }

  return (
    <>
      {/* Plan Change Modal */}

      <UpgradeModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        plans={plans?.plans}
        currentPlan={planRules?.plan_info}
      />

      <div className="space-y-6" data-testid="invoices-page">
        <div>
          <h1 className="text-2xl font-bold">Invoices</h1>
          <p className="text-muted-foreground">Manage your subscription and view billing history</p>
        </div>

        {/* Current Plan Card */}
        {currentPlan && (
          <PlanInfoCard
            currentPlan={currentPlan}
          />
        )}

        {/* Billing History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Billing History
            </CardTitle>
            <CardDescription>View all your past transactions and invoices</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice ID</TableHead>
                  <TableHead>Plan Name</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment Status</TableHead>
                  <TableHead>Payment Method</TableHead>
                  <TableHead>Transaction Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedInvoices?.map((invoice, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono text-sm">{invoice?.invoice_id}</TableCell>
                    <TableCell className="font-medium">{invoice?.plan_name}</TableCell>
                    <TableCell className="font-medium">{formatCurrency(invoice?.amount, invoice?.currency)}</TableCell>
                    <TableCell>{getStatusBadge(invoice?.status)}</TableCell>
                    <TableCell className="capitalize">{invoice?.payment_type}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(invoice?.created_at)}</TableCell>
                  </TableRow>
                ))}
                {(paginatedInvoices?.length === 0 || !paginatedInvoices) && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No invoices found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {invoices?.data?.length > 0 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <span className="text-sm text-muted-foreground">
                  {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, invoices?.data?.length)} of {invoices?.length}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                    <ChevronLeft className="w-4 h-4" /><ChevronLeft className="w-4 h-4 -ml-2" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm px-2">Page {currentPage} of {totalPages || 1}</span>
                  <Button variant="outline" size="icon" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                    <ChevronRight className="w-4 h-4" /><ChevronRight className="w-4 h-4 -ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default Invoices;